// app/javascript/projects/components/CodeNestedDisplay.jsx

import { List, AutoSizer } from 'react-virtualized';
import React, { useState, useRef, useCallback, useEffect } from 'react';

import hashString from 'utils/HashString'
import useTrackingPage from 'hooks/useTrackingPage';

import CodeLabelDropZone from './CodeLabelDropZone'
import { withToastManager } from 'ui/toast';
import TopMarginDropZone from './TopMarginDropZone'
import BottomMarginDropZone from './BottomMarginDropZone'
import CodeTransitionStyles from './CodeTransitionStyles'
import {
  BOTTOM_DROP_ZONE_HEIGHT,
  CODE_PADDING_LEFT
} from 'constants/DimensionsConstants'
import CodeListEmptyState from './CodeListEmptyState';
import useNestCode from 'hooks/codes/mutations/useNestCode';
import { useRecursiveColorChange } from 'hooks/codes/mutations/useUpdateCode';

const EMPTY_STATE_CODE_THRESHOLD = 1;

const CodeNestedDisplay = (props) => {
  const { activeTransition } = props;
  const [draggingCodeId, setDraggingCodeId] = useState(null);
  const [confirmCodeId, setConfirmCodeId] = useState(null); // Separate state for merge confirmation
  const [mergeId, setMergeId] = useState(null);
  const listRef = useRef(null);

  // Scroll to index imperatively (once) instead of using scrollToIndex prop
  useEffect(() => {
    if (props.scrollIndex !== undefined && listRef.current) {
      listRef.current.scrollToRow(props.scrollIndex);
      // Clear scrollIndex after scrolling to prevent re-scrolls
      if (props.clearScrollIndex) {
        props.clearScrollIndex();
      }
    }
  }, [props.scrollIndex, props.clearScrollIndex]);

  // Use the update code parent mutation hook
  const updateCodeParentMutation = useNestCode();

  // Add the recursive color change hook for propagating colors
  const { propagateColorToChildren } = useRecursiveColorChange(props.projectID, draggingCodeId);

  const page = useTrackingPage();

  // Function to handle nesting codes
  const nestCodes = useCallback((codeId, parent_id, position = 1) => {
    if (updateCodeParentMutation) {
      updateCodeParentMutation.mutate({
        codeId,
        parent_id,
        position,
        trackingContext: { page, surface: 'drag_drop' },
      });
    }

    if ( parent_id )
      propagateColorToChildren(parent_id);
  }, [updateCodeParentMutation, propagateColorToChildren, page]);

  const rejectNesting = useCallback(() => {
    props.toastManager.add(
      'Codes cannot be nested more than three layers', {
       appearance: 'warning',
       autoDismiss: true,
       autoDismissTimeout: 7000
     });
  }, [props.toastManager]);

  const startDrag = useCallback((codeId) => {
    props.startDrag(codeId);
    setDraggingCodeId(codeId);
  }, [props]);

  const endDrag = useCallback(() => {
    props.startDrag(null);
    setDraggingCodeId(null);
  }, [props]);

  const onShowMerge = useCallback((mergeId) => {
    setMergeId(mergeId);

    if (listRef.current) {
      listRef.current.recomputeRowHeights();
    }
  }, []);

  const cancelMerge = useCallback(() => {
    setMergeId(null);
    setConfirmCodeId(null); // Clear confirmation state
    if (listRef.current) {
      listRef.current.recomputeRowHeights();
    }
  }, []);

  const confirmMerge = useCallback(() => {
    setMergeId(null);
    setConfirmCodeId(null); // Clear confirmation state
    if (listRef.current) {
      listRef.current.recomputeRowHeights();
    }
  }, []);

  // Function to set confirm code ID for ghost state during merge confirmation
  const setConfirmDrag = useCallback((codeId) => {
    setConfirmCodeId(codeId);
  }, []);

  // Get the combined codes list including the new code if newCodeName is provided
  const getCodesWithNewCode = useCallback(() => {
    const codes = props.codes || [];
    
    if (props.newCodeName) {
      const newCode = {
        id: -1,
        name: props.newCodeName,
        newCode: true,
        level: 1,
        position: 0,
        count: 0
      };
      return [newCode, ...codes];
    }
    
    return codes;
  }, [props.codes, props.newCodeName]);

  const rowHeight = useCallback(({index}) => {
    if (index === 0) {
      return 10;
    }

    const codes = getCodesWithNewCode();
    
    // Last row is the bottom drop zone
    if (index === codes.length + 1) {
      return BOTTOM_DROP_ZONE_HEIGHT;
    }
    
    // Regular code rows
    const codeIndex = index - 1;
    const code = codes[codeIndex];
    if (!code) return 52;
    
    const NORMAL_HEIGHT = 52;
    const MERGE_HEIGHT = NORMAL_HEIGHT * 2 - 14;
    
    const mergeParent = code.id === mergeId;
    const showConfirmMerge = code.showConfirmMerge;
    
    return (mergeParent || showConfirmMerge) ? MERGE_HEIGHT : NORMAL_HEIGHT;
  }, [getCodesWithNewCode, mergeId]);

  const rowRenderer = useCallback(({
    key,         // Unique key within array of rows
    index,       // Index of row within collection
    isScrolling, // The List is currently being scrolled
    isVisible,   // This row is visible within the List (eg it is not an overscanned row)
    style        // Style object to be applied to row (to position it)
  }) => {
    if (index === 0) {
      return (
        <div style={style} key={'top'}>
          <TopMarginDropZone
            nestCodes={nestCodes}
            endDrag={endDrag}
          />
        </div>
      );
    }

    const codes = getCodesWithNewCode();
    
    // Bottom drop zone - now its own separate row
    if (index === codes.length + 1) {
      const bottomCodePosition = Math.max(
        ...(codes || []).filter(
          (code) => code.level === 1).map((code) => code.position)
      );

      return (
        <div style={style} key='bottom'>
          <BottomMarginDropZone
            bottomMargin={props.bottomMargin}
            dropPosition={bottomCodePosition + 1}
            nestCodes={nestCodes}
            endDrag={endDrag}
          />
        </div>
      );
    }
    
    // Regular code rows
    const code = codes[index - 1];
    if (!code) return <div style={style} key={key}></div>;
    
    // Only apply transition if it's recent (within 1.5 seconds to match longest animation)
    const isTransitionFresh = activeTransition?.timestamp && (Date.now() - activeTransition.timestamp < 1500);
    const transitionType = (activeTransition?.codeId && activeTransition.codeId === code.id && isTransitionFresh) 
      ? activeTransition.type : null;

    return (
      <div key={code.id} style={style}>
        <CodeLabelDropZone
          key={`code-${code.id}-${index}`}
          initiateMergeCode={props.initiateMergeCode}
          transitionType={transitionType}
          rejectNesting={rejectNesting}
          position={code.position}
          hideCodeCounts={props.hideCodeCounts}
          name={code.name}
          newCode={!!code.newCode}
          mergeParent={code.mergeParent}
          showMerge={code.showMerge}
          collapsed={code.collapsed}
          level={code.level}
          maxDepth={code.maxDepth}
          id={code.id}
          showConfirmMerge={code.showConfirmMerge}
          selected={(props.selectedCodes || []).includes(code.id)}
          onClick={!!code.newCode ? props.addNewCode : props.onClick}
          disabled={props.disabled}
          deleteCode={props.deleteCode}
          ghost={(draggingCodeId !== null && (draggingCodeId === code.id || draggingCodeId === code.parent_id || draggingCodeId === code.grandparent_id)) || 
                (confirmCodeId !== null && (confirmCodeId === code.id || confirmCodeId === code.parent_id || confirmCodeId === code.grandparent_id))}
          startDrag={startDrag}
          endDrag={endDrag}
          setConfirmDrag={setConfirmDrag}
          draggingEnabled={props.draggingEnabled}
          onCollapseClicked={props.onCollapseClicked}
          isSearching={props.isSearching}
          cancelMerge={cancelMerge}
          confirmMerge={confirmMerge}
          onShowMerge={onShowMerge}
          allowDropdown={!props.dragCode}
          nestCodes={nestCodes}
          marginNoOp={code.marginNoOp}
          hasChildren={code.hasChildren}
          isLastInSiblings={code.isLastInSiblings}
          parentPosition={code.parentPosition}
          grandparentPosition={code.grandparentPosition}
          isParentLastInSiblings={code.isParentLastInSiblings}
          parent_id={code.parent_id}
          grandparent_id={code.grandparent_id}
          codeCollapsed={code.collapsed}
        />
      </div>
    );
  }, [
    props.topMargin,
    props.initiateMergeCode,
    props.hideCodeCounts,
    props.selectedCodes,
    props.addNewCode,
    props.onClick,
    props.disabled,
    props.deleteCode,
    props.draggingEnabled,
    props.onCollapseClicked,
    props.dragCode,
    props.bottomMargin,
    getCodesWithNewCode,
    rejectNesting,
    startDrag,
    endDrag,
    draggingCodeId,
    cancelMerge,
    confirmMerge,
    onShowMerge,
    nestCodes,
    props.activeTransition?.codeId,
    props.activeTransition?.type,
  ]);

  const NORMAL_HEIGHT = 52;
  const codes = getCodesWithNewCode().map((code) => {
    return {
      id: code.id,
      collapsed: code.collapsed,
      level: code.level,
      count: code.count,
    }
  });

  const hash = hashString(JSON.stringify({
    selcted: props.selectedCodes,
    codes: codes,
    newCodeName: props.newCodeName,
    activeTransition: props.activeTransition
  }));

  // Calculate list height based on number of codes
  const getListHeight = (containerHeight) => {
    return codes.length === 0 
      ? 0
      : codes.length <= EMPTY_STATE_CODE_THRESHOLD 
        ? NORMAL_HEIGHT * (codes.length + 2)
        : containerHeight;
  };

  return (
    <div style={{height: '100%', overflow: 'hidden'}}>
      <CodeTransitionStyles />
      <AutoSizer>
        {({ height, width }) => (
          <div style={{padding: "0px", paddingLeft: CODE_PADDING_LEFT}}>
            <List
              width={345}
              height={getListHeight(height)}
              rowCount={codes.length + 2}
              rowHeight={rowHeight}
              rowRenderer={rowRenderer}
              searchText={props.searchText}
              disabled={props.disabled}
              draggingEnabled={props.draggingEnabled}
              dragCode={props.dragCode}
              codeHash={hash}
              hideCodeCounts={props.hideCodeCounts}
              ref={listRef}
              scrollToAlignment={'start'}
            />
          </div>
        )}
      </AutoSizer>
      <CodeListEmptyState 
        codes={codes}
        searchText={props.searchText}
        showEmptyStateOnboarding={props.showEmptyStateOnboarding}
      />
    </div>
  );
};

export default withToastManager(CodeNestedDisplay);